import {
  GUILD_WAR_PATH_REGISTRY,
  type MartialPathCode as CoreMartialPathCode,
  type MartialPathKey,
} from "@/core/guild-war/path-registry"

import type {
  AbsenceDay,
  AbsenceState,
  CandidatePoolMember,
  CandidatePoolResponse,
  CandidatePoolSecondaryPathFilter,
  CandidatePoolSortBy,
  CandidatePoolSortOrder,
  CandidateSource,
  GuildDirectoryMember,
  GuildWarMemberDashboard,
  GuildWarMemberIdentity,
  GuildWarMemberProfileIdentity,
  GuildWarPermanentMembershipRecord,
  GuildWarRosterBoard,
  GuildWarRosterBoardRecord,
  GuildWarSignupProfile,
  GuildWarWeeklySignupMutationResult,
  GuildWarWeeklySignupRecord,
  GuildWarWeek,
  ManualParticipantMode,
  MartialArtPath,
  MartialPathCode,
  MartialPathIdentifier,
  PermanentApplication,
  PermanentApplicationState,
  PermanentMember,
  PermanentWithdrawResult,
  ReminderSendResult,
  UnsignedMember,
  UnsignedMemberListResult,
  WeeklyAvailability,
  WeekState,
} from "./guild-war-wire-types.js"

import { http } from "./http-client"

export type {
  AbsenceDay,
  AbsenceState,
  CandidatePoolMember,
  CandidatePoolResponse,
  CandidatePoolSecondaryPathFilter,
  CandidatePoolSortBy,
  CandidatePoolSortOrder,
  CandidateSource,
  GuildDirectoryMember,
  GuildWarMemberDashboard,
  GuildWarMemberIdentity,
  GuildWarMemberProfileIdentity,
  GuildWarPermanentMembershipRecord,
  GuildWarRosterBoard,
  GuildWarRosterBoardRecord,
  GuildWarRosterMemberPathSnapshot,
  GuildWarRosterSubteam,
  GuildWarSignupProfile,
  GuildWarTeamRoster,
  GuildWarWeeklySignupMutationResult,
  GuildWarWeeklySignupRecord,
  GuildWarWeek,
  ManualParticipantMode,
  MartialArtPath,
  MartialPathCode,
  MartialPathIdentifier,
  PermanentApplication,
  PermanentApplicationState,
  PermanentMember,
  PermanentWithdrawResult,
  ReminderSendResult,
  UnsignedMember,
  UnsignedMemberListResult,
  WeeklyAvailability,
  WeekState,
} from "./guild-war-wire-types.js"

export const MARTIAL_ART_PATHS: ReadonlyArray<MartialPathKey> =
  GUILD_WAR_PATH_REGISTRY.map((entry) => entry.key)

export const MARTIAL_PATH_CODES: ReadonlyArray<CoreMartialPathCode> =
  GUILD_WAR_PATH_REGISTRY.map((entry) => entry.code)

export class GuildWarApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message)
    this.name = "GuildWarApiError"
  }
}

const getEdenErrorPayload = (
  error: unknown,
): {
  message?: string
  code?: string
} => {
  if (!error || typeof error !== "object") {
    return {}
  }

  const maybeError = error as { value?: unknown; message?: unknown }
  const payload: { message?: string; code?: string } = {}

  if (typeof maybeError.message === "string" && maybeError.message.length > 0) {
    payload.message = maybeError.message
  }

  if (maybeError.value && typeof maybeError.value === "object") {
    const payload = maybeError.value as {
      error?: { message?: unknown; code?: unknown }
      message?: unknown
    }

    if (typeof payload.error?.message === "string") {
      return {
        message: payload.error.message,
        code:
          typeof payload.error.code === "string"
            ? payload.error.code
            : undefined,
      }
    }

    if (typeof payload.message === "string") {
      return { message: payload.message }
    }
  }

  return payload
}

const ensureEdenData = <T>(
  response: { data: T | null | undefined; error: unknown },
  fallbackMessage: string,
): NonNullable<T> => {
  if (response.data != null) {
    return response.data as NonNullable<T>
  }

  const payload = getEdenErrorPayload(response.error)
  throw new GuildWarApiError(payload.message ?? fallbackMessage, payload.code)
}

export const guildWarApi = {
  listWeeks: async (): Promise<GuildWarWeek[]> => {
    const data = ensureEdenData(
      await http.api["guild-war"].weeks.get(),
      "Failed to load weeks",
    )

    if ("items" in data) {
      return data.items
    }

    throw new Error("Failed to load weeks")
  },
  createWeek: async (input: {
    weekId: string
    weekStartAt: Date
    weekEndAt: Date
  }): Promise<{ week: GuildWarWeek; warnings: string[] }> => {
    const r = await http.api["guild-war"].weeks.post({
      weekId: input.weekId,
      weekEndAt: input.weekEndAt,
      weekStartAt: input.weekStartAt,
    })

    const data = ensureEdenData(r, "Failed to create week")
    if ("item" in data) {
      return {
        week: data.item as GuildWarWeek,
        warnings: (data as { warnings?: string[] }).warnings ?? [],
      }
    }

    throw new Error("Failed to create week")
  },
  transitionWeek: async (weekId: string, toState: WeekState) => {
    const r = await http.api["guild-war"].weeks({ weekId }).transition.post({
      toState,
    })

    const data = ensureEdenData(r, "Failed to transition week")
    if ("item" in data) {
      return data.item as GuildWarWeek
    }

    throw new Error("Failed to transition week")
  },
  getRosterBoard: async (
    rosterDate: string,
  ): Promise<GuildWarRosterBoardRecord> => {
    const data = ensureEdenData(
      await http.api["guild-war"].rosters({ rosterDate }).get(),
      "Failed to load roster board",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to load roster board")
  },
  saveRosterDraft: async (
    rosterDate: string,
    input: {
      board: GuildWarRosterBoard
      sourceWeekId?: string | null
      displayDate?: string | null
      expectedVersion: number
    },
  ): Promise<GuildWarRosterBoardRecord> => {
    const data = ensureEdenData(
      await http.api["guild-war"].rosters({ rosterDate }).draft.put({
        board: input.board as never,
        sourceWeekId: input.sourceWeekId ?? null,
        displayDate: input.displayDate ?? null,
        expectedVersion: input.expectedVersion,
      }),
      "Failed to save roster draft",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to save roster draft")
  },
  publishRosterBoard: async (
    rosterDate: string,
    input: { expectedVersion: number },
  ): Promise<GuildWarRosterBoardRecord> => {
    const data = ensureEdenData(
      await http.api["guild-war"].rosters({ rosterDate }).publish.post({
        expectedVersion: input.expectedVersion,
      }),
      "Failed to publish roster",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to publish roster")
  },
  getPublishedRosterBoard: async (
    rosterDate: string,
  ): Promise<GuildWarRosterBoardRecord> => {
    const data = ensureEdenData(
      await http.api["guild-war"].rosters({ rosterDate }).published.get(),
      "Failed to load published roster",
    )

    if ("item" in data) {
      return data.item
    }

    throw new Error("Failed to load published roster")
  },
  getCandidatePool: async (params: {
    weekId: string
    day?: AbsenceDay
    source?: CandidateSource
    skillPaths?: MartialArtPath | MartialArtPath[]
    secondaryPath?: CandidatePoolSecondaryPathFilter
    absenceState?: AbsenceState
    search?: string
    sortBy?: CandidatePoolSortBy
    sortOrder?: CandidatePoolSortOrder
    page?: number
    pageSize?: number
  }): Promise<CandidatePoolResponse> => {
    const query: {
      weekId: string
      day?: AbsenceDay
      source?: CandidateSource
      skillPaths?: MartialArtPath[]
      secondaryPath?: CandidatePoolSecondaryPathFilter
      absenceState?: AbsenceState
      search?: string
      sortBy: CandidatePoolSortBy
      sortOrder: CandidatePoolSortOrder
      page: number
      pageSize: number
    } = {
      weekId: params.weekId,
      sortBy: params.sortBy ?? "member",
      sortOrder: params.sortOrder ?? "asc",
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    }

    if (params.day) query.day = params.day
    if (params.source) query.source = params.source

    if (Array.isArray(params.skillPaths)) {
      if (params.skillPaths.length > 0) {
        query.skillPaths = params.skillPaths
      }
    } else if (params.skillPaths) {
      query.skillPaths = [params.skillPaths]
    }

    if (params.secondaryPath) query.secondaryPath = params.secondaryPath
    if (params.absenceState) query.absenceState = params.absenceState
    if (params.search && params.search.trim().length > 0)
      query.search = params.search.trim()

    const data = ensureEdenData(
      await http.api["guild-war"]["candidate-pool"].get({ query }),
      "Failed to load candidate pool",
    )

    if (
      "items" in data &&
      "total" in data &&
      "page" in data &&
      "pageSize" in data
    ) {
      return data as CandidatePoolResponse
    }

    throw new Error("Failed to load candidate pool")
  },
  searchGuildMembers: async (params: {
    query: string
    limit?: number
  }): Promise<GuildDirectoryMember[]> => {
    const data = ensureEdenData(
      await http.api["guild-war"].members.search.get({
        query: {
          query: params.query,
          limit: params.limit ?? 10,
        },
      }),
      "Failed to search guild members",
    )

    if ("items" in data) {
      return data.items
    }

    throw new Error("Failed to search guild members")
  },
  listGuildWarMemberIdentitiesByIds: async (
    ids: string[],
  ): Promise<GuildWarMemberProfileIdentity[]> => {
    const uniqueIds = [
      ...new Set(ids.map((value) => value.trim()).filter(Boolean)),
    ]
    if (uniqueIds.length === 0) {
      return []
    }

    const data = ensureEdenData(
      await http.api["guild-war"].members["by-ids"].get({
        query: {
          ids: uniqueIds,
        },
      }),
      "Failed to load guild war members",
    )

    if ("items" in data) {
      return data.items
    }

    throw new Error("Failed to load guild war members")
  },
  setWeeklySignupForMember: async (
    memberId: string,
    input: {
      weekId: string
      availability: WeeklyAvailability
      skillPath?: MartialArtPath | null
      secondarySkillPath?: MartialArtPath | null
      overrideSavedPaths?: boolean
      discordUsername?: string | null
      guildDisplayName?: string | null
    },
  ): Promise<GuildWarWeeklySignupMutationResult> => {
    const data = ensureEdenData(
      await http.api["guild-war"].signups({ memberId }).put({
        weekId: input.weekId,
        availability: input.availability,
        skillPath: input.skillPath ?? null,
        secondarySkillPath: input.secondarySkillPath ?? null,
        overrideSavedPaths: input.overrideSavedPaths ?? false,
        discordUsername: input.discordUsername,
        guildDisplayName: input.guildDisplayName,
      }),
      "Failed to save weekly signup",
    )

    if ("item" in data) {
      return {
        item: data.item as GuildWarWeeklySignupRecord,
      }
    }

    throw new Error("Failed to save weekly signup")
  },
  withdrawWeeklySignupForMember: async (memberId: string, weekId: string) => {
    const data = ensureEdenData(
      await http.api["guild-war"].signups({ memberId }).withdraw.post({
        weekId,
      }),
      "Failed to withdraw weekly signup",
    )

    if ("item" in data) {
      return data.item as GuildWarWeeklySignupRecord
    }

    throw new Error("Failed to withdraw weekly signup")
  },
  getMyDashboard: async (): Promise<GuildWarMemberDashboard> => {
    const data = ensureEdenData(
      await http.api["guild-war"].me.dashboard.get(),
      "Failed to load guild war dashboard",
    )

    if ("item" in data) {
      const item = data.item as GuildWarMemberDashboard
      return {
        ...item,
        week: item.week,
      }
    }

    throw new Error("Failed to load guild war dashboard")
  },
  setMyProfilePaths: async (input: {
    primaryPath: MartialArtPath | null
    secondaryPath: MartialArtPath | null
  }): Promise<GuildWarSignupProfile> => {
    const data = ensureEdenData(
      await http.api["guild-war"].me["profile-paths"].put({
        primaryPath: input.primaryPath,
        secondaryPath: input.secondaryPath,
      }),
      "Failed to update guild war profile paths",
    )

    if ("item" in data) {
      return data.item as GuildWarSignupProfile
    }

    throw new Error("Failed to update guild war profile paths")
  },
  withdrawMyPermanent: async (): Promise<PermanentWithdrawResult> => {
    const data = ensureEdenData(
      await http.api["guild-war"].me.permanent.withdraw.post(),
      "Failed to withdraw permanent status",
    )

    if ("item" in data) {
      return data.item as PermanentWithdrawResult
    }

    throw new Error("Failed to withdraw permanent status")
  },
  applyPermanentMembership: async (input: {
    weekId?: string
    expiresAt: Date
    skillPathAtApply: MartialArtPath | null
    note?: string | null
  }): Promise<PermanentApplication> => {
    const data = ensureEdenData(
      await http.api["guild-war"]["permanent-applications"].apply.post({
        weekId: input.weekId,
        expiresAt: input.expiresAt,
        skillPathAtApply: input.skillPathAtApply,
        note: input.note ?? null,
      }),
      "Failed to submit permanent membership application",
    )

    if ("item" in data) {
      return data.item as PermanentApplication
    }

    throw new Error("Failed to submit permanent membership application")
  },
  grantPermanentMembershipForMember: async (input: {
    memberId: string
    weekId: string
    skillPath?: MartialArtPath | null
    secondarySkillPath?: MartialArtPath | null
    overrideSavedPaths?: boolean
    discordUsername?: string | null
    guildDisplayName?: string | null
  }) => {
    const data = ensureEdenData(
      await http.api["guild-war"]["permanent-memberships"].post({
        memberId: input.memberId,
        weekId: input.weekId,
        skillPath: input.skillPath ?? null,
        secondarySkillPath: input.secondarySkillPath ?? null,
        overrideSavedPaths: input.overrideSavedPaths ?? false,
        discordUsername: input.discordUsername ?? null,
        guildDisplayName: input.guildDisplayName ?? null,
      }),
      "Failed to grant permanent membership",
    )

    if ("membership" in data) {
      return data
    }

    throw new Error("Failed to grant permanent membership")
  },
  listPermanentMembers: async (): Promise<PermanentMember[]> => {
    const data = ensureEdenData(
      await http.api["guild-war"]["permanent-memberships"].get(),
      "Failed to load permanent members",
    )

    if ("items" in data) {
      return data.items
    }

    throw new Error("Failed to load permanent members")
  },
  listPermanentMembershipApplications: async (params?: {
    state?: PermanentApplicationState
    limit?: number
  }): Promise<PermanentApplication[]> => {
    const query: {
      state?: PermanentApplicationState
      limit?: number
    } = {}

    if (params?.state) query.state = params.state
    if (params?.limit) query.limit = params.limit

    const data = ensureEdenData(
      await http.api["guild-war"]["permanent-applications"].get({ query }),
      "Failed to load permanent membership applications",
    )

    if ("items" in data) {
      return data.items
    }

    throw new Error("Failed to load permanent membership applications")
  },
  revokePermanentMembership: async (membershipId: string) => {
    const data = ensureEdenData(
      await http.api["guild-war"]["permanent-memberships"].revoke.post({
        membershipId,
      }),
      "Failed to revoke permanent membership",
    )
    if ("item" in data) {
      return data.item as GuildWarPermanentMembershipRecord
    }
    throw new Error("Failed to revoke permanent membership")
  },
  reviewPermanentMembershipApplication: (
    applicationId: string,
    input: {
      decision: "approved" | "rejected"
      reviewNote?: string
    },
  ) => {
    return http.api["guild-war"]["permanent-applications"].review
      .post({
        applicationId,
        decision: input.decision,
        reviewNote: input.reviewNote ?? null,
      })
      .then((response) => {
        return ensureEdenData(
          response,
          "Failed to review permanent membership application",
        )
      })
  },
  getUnsignedMembers: async (): Promise<UnsignedMemberListResult> => {
    const data = ensureEdenData(
      await http.api["guild-war"].reminders.unsigned.get(),
      "Failed to load unsigned members",
    )

    if ("item" in data) {
      return data.item as UnsignedMemberListResult
    }

    throw new Error("Failed to load unsigned members")
  },
  sendReminders: async (input: {
    memberIds: string[]
    weekId: string
  }): Promise<ReminderSendResult> => {
    const data = ensureEdenData(
      await http.api["guild-war"].reminders.send.post({
        memberIds: input.memberIds,
        weekId: input.weekId,
      }),
      "Failed to send reminders",
    )

    if ("item" in data) {
      return data.item as ReminderSendResult
    }

    throw new Error("Failed to send reminders")
  },
}
